import '../../../css/mainCanvas.css'

import BaseCanvas from './BaseCanvas'
import BackgroundCanvas from './BackgroundCanvas';
import FrequencyResponse from './FrequencyResponse';
import Analyzer from './Analyzer';

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUserSetting } from '../../../features/counter/eqFiltersReducer'

const GRID_COLOR = 'rgba(125, 163, 171, 0.27)'
const TEXT_COLOR = 'rgb(255, 255, 255)'
const BALL_COLOR = 'rgb(153, 150, 150)'

const MainCanvas = ({ audioEnv }) => {
    const globalMode = useSelector((state) => state.filters.globalMode);
    // Accessing filters from Redux and setting up the dispatch function
    const filters = useSelector((state) => state.filters.userSetting);

    const dispatch = useDispatch();
    const [draggingIndex, setDraggingIndex] = useState(null);

    // Frequency values for labeling and mapping
    const frequencies = useMemo(() => [
        20, ...Array.from({ length: 9 }, (_, i) => 20 + 10 * i),
        ...Array.from({ length: 9 }, (_, i) => 200 + 100 * i),
        ...Array.from({ length: 9 }, (_, i) => 2000 + 1000 * i), 20000
    ], []);

    const freqLabels = useMemo(() => [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000], []);
    const ampLabels = useMemo(() => [12, 6, 0, -6, -12], []);

    const configRef = useRef({ freq: null, amp: null });
    // Initialize frequency or amplitude ranges
    const initRange = (ctx, xStartRatio, xEndRatio, yStartRatio, yEndRatio) => ({
        xStart: Math.floor(ctx.canvas.width * xStartRatio),
        xEnd: Math.floor(ctx.canvas.width * xEndRatio),
        yStart: Math.floor(ctx.canvas.height * yStartRatio),
        yEnd: Math.floor(ctx.canvas.height * yEndRatio),
    });

    // Map frequency to X coordinate
    const mapFreqToX = useCallback((freq, xStart, xEnd) => {
        const minFreq = frequencies[0];
        const maxFreq = frequencies[frequencies.length - 1];
        const normalizedX = (Math.log10(freq) - Math.log10(minFreq)) / (Math.log10(maxFreq) - Math.log10(minFreq));
        return xStart + normalizedX * (xEnd - xStart);
    }, [frequencies])

    /**
 * Convert an X coordinate to a frequency value
 * @param {number} x - The X coordinate on the canvas
 * @param {number} freqStart - Starting X coordinate for frequency
 * @param {number} freqEnd - Ending X coordinate for frequency
 * @return {number} - The frequency value corresponding to the X coordinate
 */
    const mapXToFreq = useCallback((x, freqStart, freqEnd) => {
        const minFreq = frequencies[0];
        const maxFreq = frequencies[frequencies.length - 1];
        const normalizedX = (x - freqStart) / (freqEnd - freqStart);
        const logFreq = normalizedX * (Math.log10(maxFreq) - Math.log10(minFreq)) + Math.log10(minFreq);
        return Math.pow(10, logFreq);
    }, [frequencies]);


    // Map amplitude to Y coordinate
    const mapAmpToY = useCallback((amp, yStart, yEnd) => {
        const minAmp = ampLabels[ampLabels.length - 1];
        const maxAmp = ampLabels[0];
        const normalizedY = (amp - maxAmp) / (minAmp - maxAmp);
        return yStart + normalizedY * (yEnd - yStart);
    }, [ampLabels]);

    /**
     * Convert a Y coordinate to an amplitude value
     * @param {number} y - The Y coordinate on the canvas
     * @param {number} ampStartY - Starting Y coordinate for amplitude
     * @param {number} ampEndY - Ending Y coordinate for amplitude
     * @return {number} - The amplitude value corresponding to the Y coordinate
     */
    const mapYToAmp = useCallback((y, ampStart, ampEnd) => {
        const minAmp = ampLabels[ampLabels.length - 1];
        const maxAmp = ampLabels[0];

        const normalizedY = (y - ampStart) / (ampEnd - ampStart);
        return maxAmp + normalizedY * (minAmp - maxAmp);
    }, [ampLabels]);

    /**
     * Maps frequency and amplitude to canvas coordinates
     * @param {CanvasRenderingContext2D} ctx - The canvas rendering context
     * @param {number} frequency - The frequency value to map
     * @param {number} amplitude - The amplitude value to map
     * @return {Object} - X and Y coordinates on the canvas
     */
    const mapToCanvas = useCallback((ctx, frequency, amplitude) => {
        const { xStart: freqStartX, xEnd: freqEndX } = initRange(ctx, 0.05, 0.95, 0.05, 0.95);
        const { yStart: ampStartY, yEnd: ampEndY } = initRange(ctx, 0.02, 0.98, 0.1, 0.9);
        return {
            x: mapFreqToX(frequency, freqStartX, freqEndX),
            y: mapAmpToY(amplitude, ampStartY, ampEndY),
        };
    }, [mapFreqToX, mapAmpToY]);

    /**
     * Maps canvas coordinates to frequency and amplitude values
     * @param {CanvasRenderingContext2D} ctx - The canvas rendering context
     * @param {number} x - X coordinate on the canvas
     * @param {number} y - Y coordinate on the canvas
     * @return {Object} - Frequency and amplitude values
     */
    const mapToFreqAndAmp = useCallback((ctx, x, y) => {
        const { xStart: freqStartX, xEnd: freqEndX } = initRange(ctx, 0.05, 0.95, 0.05, 0.95);
        const { yStart: ampStartY, yEnd: ampEndY } = initRange(ctx, 0.02, 0.98, 0.1, 0.9);

        return {
            frequency: mapXToFreq(x, freqStartX, freqEndX),
            amplitude: mapYToAmp(y, ampStartY, ampEndY),
        };
    }, [mapXToFreq, mapYToAmp]);

    /**
     * Draw a line on the canvas
     * @param {CanvasRenderingContext2D} ctx - The canvas rendering context
     * @param {number} x1 - Starting X coordinate
     * @param {number} y1 - Starting Y coordinate
     * @param {number} x2 - Ending X coordinate
     * @param {number} y2 - Ending Y coordinate
     * @param {number} width - Line width
     * @param {string} color - Line color
     */
    const drawLine = (ctx, x1, y1, x2, y2, width, color) => {
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
    };

    // Draw grid (frequency or amplitude)
    const drawGrid = useCallback((ctx, config, isFrequency) => {
        const { xStart, xEnd, yStart, yEnd } = config;
        const lines = isFrequency ? frequencies : ampLabels;
        const labels = isFrequency ? freqLabels : ampLabels;
        const mapFunc = isFrequency ? mapFreqToX : mapAmpToY;

        lines.forEach((line) => {
            const coord = isFrequency ? mapFunc(line, xStart, xEnd) : mapFunc(line, yStart, yEnd);
            const isFreq = isFrequency ? [coord, yStart, coord, yEnd] : [xStart, coord, xEnd, coord];
            drawLine(ctx, ...isFreq, 1, GRID_COLOR);
        });

        labels.forEach((label) => {
            const coord = isFrequency ? mapFunc(label, xStart, xEnd) : mapFunc(label, yStart, yEnd);
            isFrequency ? ctx.textAlign = "center" : ctx.textAlign = "right";
            ctx.fillStyle = TEXT_COLOR;
            ctx.fillText(label > 1000 ? `${label / 1000}k` : label, isFrequency ? coord : xEnd + 25, isFrequency ? yEnd + 10 : coord + 5);
        });

    }, [ampLabels, freqLabels, frequencies, mapAmpToY, mapFreqToX]);

    /**
     * Pre-renders the frequency and amplitude grid on a background image
     * @param {CanvasRenderingContext2D} ctx - The canvas rendering context
     * @param {number} width - Canvas width
     * @param {number} height - Canvas height
     * @return {ImageData} - Cached background image data
     */
    const preRenderBackground = useCallback((ctx) => {
        const freqConfig = initRange(ctx, 0.05, 0.95, 0.05, 0.95);
        const ampConfig = initRange(ctx, 0.02, 0.98, 0.1, 0.9);
        configRef.current = { freq: freqConfig, amp: ampConfig };

        drawGrid(ctx, freqConfig, true); // Frequency grid
        drawGrid(ctx, ampConfig, false); // Amplitude grid
        return ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
    }, [drawGrid]);

    /**
     * Draws a draggable ball on the canvas
     * @param {CanvasRenderingContext2D} ctx - The canvas rendering context
     * @param {number} x - X coordinate of the ball
     * @param {number} y - Y coordinate of the ball
     * @param {number} radius - Radius of the ball
     * @param {string} color - Color of the ball
     */ 
    const drawBall = (ctx, x, y, index, radius = 13, color = BALL_COLOR) => {
        // Draw the ball
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2, false);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.closePath();
    

        ctx.fillStyle = "white"; // Text color
        ctx.font = "light 15px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(index, x, y); // Place text at center
    };
    

    const handleMouseDrag = (ctx, x, y) => {
        if (draggingIndex !== null) {
            const { freq, amp } = configRef.current;
            const constrainedX = Math.max(freq.xStart, Math.min(x, freq.xEnd));
            const constrainedY = Math.max(amp.yStart, Math.min(y, amp.yEnd));
            const { frequency, amplitude } = mapToFreqAndAmp(ctx, constrainedX, constrainedY);

            const updatedFilters = filters.map((filter, index) =>
                index === draggingIndex ? { ...filter, frequency, gain: amplitude } : filter
            );

            dispatch(setUserSetting(updatedFilters));
        }
    };

    /**
     * Main function to draw the canvas elements including points
     * @param {CanvasRenderingContext2D} ctx - The canvas rendering context
     */
    const drawHandles = (ctx) => {
        filters.forEach((filter, index) => {
            const { x, y } = mapToCanvas(ctx, filter.frequency, filter.gain);
            if(!filter.bypass) {
                drawBall(ctx, x, y, index + 1);
            }
            
        });
    };

    useEffect(() => {
        return () => {
            console.log("Cleaning up canvases...");
            const canvasList = document.querySelectorAll("canvas");
            canvasList.forEach(canvas => {
                const ctx = canvas.getContext("2d");
                if (ctx) {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                }
            });
        };
    }, []);


    return (
        <div className="canvas-container">
            <Analyzer
                audioEnv={audioEnv}
                globalMode={globalMode}
                mapToCanvas={mapToCanvas}
                mapFreqToX={mapFreqToX}
            />

            <BackgroundCanvas
                preRenderBackground={preRenderBackground}
            />

            <FrequencyResponse
                audioEnv={audioEnv}
                globalMode={globalMode}
                mapToFreqAndAmp={mapToFreqAndAmp}
                mapToCanvas={mapToCanvas}
            />

            <BaseCanvas
                drawHandles={drawHandles}
                globalMode={globalMode}
                filters={filters} // Passing points
                canvasMapping={mapToCanvas}
                onDrag={handleMouseDrag}
                draggingIndex={draggingIndex} // Passing draggingIndex
                setDraggingIndex={setDraggingIndex} // Passing setDraggingIndex
            />
        </div>
    );
};

export default MainCanvas;

